import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Store, Factory, Globe2, AlertCircle, Wrench, Boxes, Trophy, ArrowRight } from "lucide-react";
import { PROFILES, TONES, IMAGES } from "@/data/content";
import { GoldButton } from "@/components/Section";
import { Fragment } from "@/components/Decor";

const ICONS = { store: Store, factory: Factory, globe: Globe2 };

const Panel = ({ icon: Icon, label, items, tone, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`rounded-[24px] border border-[#0E1B31]/[0.07] bg-white p-6 shadow-[0_20px_50px_-40px_rgba(14,27,49,0.35)] ${className}`}
  >
    <p className="flex items-center gap-2 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6B7A8F]">
      <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ background: tone.tint, color: tone.accent }}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      {label}
    </p>
    <ul className="mt-4 space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-sm leading-snug text-[#3B4A5E]">
          <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.accent }} />
          {it}
        </li>
      ))}
    </ul>
  </motion.div>
);

export const ProfilesExplorer = () => {
  const [active, setActive] = useState(0);
  const p = PROFILES[active];
  const t = TONES[p.tone];

  return (
    <div data-testid="profiles-explorer">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="tablist">
        {PROFILES.map((pr, i) => {
          const Icon = ICONS[pr.icon];
          const tt = TONES[pr.tone];
          const on = i === active;
          return (
            <button
              key={pr.id}
              role="tab"
              aria-selected={on}
              data-testid={`profile-tab-${pr.id}`}
              onClick={() => setActive(i)}
              className={`group relative overflow-hidden rounded-[22px] border p-5 text-left transition-all duration-500 ${on ? "text-white border-transparent shadow-[0_24px_50px_-30px_rgba(14,27,49,0.5)] -translate-y-1" : "bg-white border-[#0E1B31]/[0.08] hover:border-[#0E1B31]/25"}`}
              style={{ background: on ? `linear-gradient(135deg, ${tt.accent}, ${tt.accent}CC)` : undefined }}
            >
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${on ? "bg-white/15" : ""}`} style={{ background: on ? undefined : tt.tint, color: on ? "#fff" : tt.accent }}>
                <Icon className="h-5 w-5" />
              </span>
              <span className={`mt-4 block font-mono-brand text-[10px] tracking-[0.25em] uppercase ${on ? "text-white/70" : "text-[#6B7A8F]"}`}>0{i + 1}</span>
              <span className={`mt-1 block font-display text-xl font-semibold leading-tight ${on ? "text-white" : "text-[#0E1B31]"}`}>{pr.title}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={p.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="profile-panel"
          className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden rounded-[30px] h-[320px] lg:h-full min-h-[420px]">
              <motion.img
                key={p.image}
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                src={IMAGES[p.image]}
                alt={p.title}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 35%, ${t.accent}EE 100%)` }} />
              <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase text-white/70">Ποιοι είναι</p>
                <p className="mt-2 font-display text-2xl italic leading-snug">{p.tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/85">{p.intro}</p>
              </div>
            </div>
            <Fragment testId="profile-outcome-fragment" className="absolute -right-3 top-6 hidden lg:block w-[210px] float-mid">
              <p className="flex items-center gap-2 font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]"><Trophy className="h-3 w-3" style={{ color: t.accent }} /> Αποτέλεσμα</p>
              <p className="mt-1.5 text-[12px] font-medium leading-snug text-[#0E1B31]">{p.outcomes[0]}</p>
            </Fragment>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Panel icon={AlertCircle} label="Τυπικές προκλήσεις" items={p.challenges} tone={t} delay={0.05} />
            <Panel icon={Wrench} label="Τι βελτιώνει η CRESTIA" items={p.improves} tone={t} delay={0.12} />
            <Panel icon={Boxes} label="Πρακτικοί τομείς υποστήριξης" items={p.areas} tone={t} delay={0.19} />
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.26 }}
              className="relative overflow-hidden rounded-[24px] p-6 text-white flex flex-col"
              style={{ background: `linear-gradient(150deg, #0E1B31, ${t.accent})` }}
            >
              <p className="flex items-center gap-2 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white/70">
                <Trophy className="h-3.5 w-3.5 text-[#C5A059]" /> Αναμενόμενα αποτελέσματα
              </p>
              <ul className="mt-4 space-y-2.5 flex-1">
                {p.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-2.5 text-sm leading-snug text-white/90">
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-[#C5A059]" />
                    {o}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <GoldButton to={`/contact?interest=${encodeURIComponent(p.title)}`} testId={`profile-cta-${p.id}`} variant="light" className="!px-5 !py-2.5 !text-xs">
                  Συζητήστε την περίπτωσή σας
                </GoldButton>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
